import React, { useState, useMemo } from 'react';
import {
    Table,
    Tag,
    Button,
    Space,
    Drawer,
    Alert,
    Popconfirm,
    message,
    Tooltip,
    Typography,
    Spin,
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ArrowRightOutlined,
    InfoCircleOutlined,
    WarningOutlined,
    QrcodeOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { listTransitions, deleteTransition } from '../../../config/services/patrolService';
import { parseApiError } from '../../../config/services/errorHandler';
import TransitionForm from './TransitionForm';
import QrPanel from '../Checkpoints/QrPanel';
import { useSelector } from 'react-redux';
import useColors from '../../../hooks/useColors';

const { Text } = Typography;

/**
 * PatrolRouteTab — shown inside Property Detail Tabs.
 *
 * Props:
 *   propertyId   {number}
 *   checkpoints  {array}  all active checkpoints [{id, name, isStartingPoint}]
 */
const PatrolRouteTab = ({ propertyId, checkpoints = [] }) => {
    const { colorPrimary } = useColors();
    const queryClient = useQueryClient();
    const user = useSelector((state) => state.AppReducer?.user);
    const isRole = user?.userRole?.[0];
    const canWrite = isRole === 'admin' || isRole === 'manager' || isRole === 'supervisor';

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [qrTarget, setQrTarget] = useState(null); // full checkpoint record

    // Full checkpoint records (with location/status/etc.) keyed by id, so the QR
    // panel can show full checkpoint details from the transitions table.
    const checkpointsById = useMemo(() => {
        const map = {};
        checkpoints.forEach((c) => { map[c.id] = c; });
        return map;
    }, [checkpoints]);

    const openQr = (checkpointRef) => {
        if (!checkpointRef?.id) return;
        setQrTarget(checkpointsById[checkpointRef.id] ?? checkpointRef);
    };

    const { data: transitions = [], isLoading } = useQuery(
        ['checkpoint-transitions', propertyId],
        () => listTransitions(propertyId),
        { enabled: !!propertyId }
    );

    // ── Derived warnings ──────────────────────────────────────
    const activeCheckpoints = useMemo(
        () => checkpoints.filter((c) => c.status === 'active'),
        [checkpoints]
    );

    const startingPoint = activeCheckpoints.find((c) => c.isStartingPoint);

    const warnings = useMemo(() => {
        const msgs = [];
        const activeTransitions = transitions.filter((t) => t.isActive);

        // Checkpoints with no outgoing transitions
        const withOutgoing = new Set(activeTransitions.map((t) => t.fromCheckpoint?.id));
        const noOutgoing = activeCheckpoints.filter((c) => !withOutgoing.has(c.id));
        if (noOutgoing.length > 0) {
            msgs.push(`No outgoing routes from: ${noOutgoing.map((c) => c.name).join(', ')}`);
        }

        // Checkpoints with no incoming transitions
        const withIncoming = new Set(activeTransitions.map((t) => t.toCheckpoint?.id));
        const noIncoming = activeCheckpoints.filter((c) => !withIncoming.has(c.id));
        if (noIncoming.length > 0) {
            msgs.push(`No incoming routes to: ${noIncoming.map((c) => c.name).join(', ')}`);
        }

        // No return path to starting point
        if (startingPoint) {
            const hasReturn = activeTransitions.some((t) => t.toCheckpoint?.id === startingPoint.id);
            if (!hasReturn) {
                msgs.push(`No route leads back to starting point "${startingPoint.name}".`);
            }
        }

        return msgs;
    }, [transitions, activeCheckpoints, startingPoint]);

    // ── Existing groups ───────────────────────────────────────
    const existingGroups = useMemo(() => {
        const groups = new Set();
        transitions.forEach((t) => {
            if (t.alternativeGroup) groups.add(t.alternativeGroup);
        });
        return [...groups];
    }, [transitions]);

    // ── Delete ────────────────────────────────────────────────
    const deleteMutation = useMutation(
        (transitionId) => deleteTransition(propertyId, transitionId),
        {
            onSuccess: () => {
                message.success('Transition removed.');
                queryClient.invalidateQueries(['checkpoint-transitions', propertyId]);
            },
            onError: (err) => {
                const parsed = parseApiError(err);
                message.error(parsed.message);
            },
        }
    );

    const openCreate = () => { setEditTarget(null); setDrawerOpen(true); };
    const openEdit = (row) => { setEditTarget(row); setDrawerOpen(true); };
    const closeDrawer = () => { setDrawerOpen(false); setEditTarget(null); };
    const handleFormSuccess = () => { closeDrawer(); };

    // ── Columns ───────────────────────────────────────────────
    const columns = [
        {
            title: 'From',
            key: 'from',
            render: (_, r) => (
                <Space size={6}>
                    <Text strong style={{ color: r.isActive ? 'inherit' : '#9ca3af' }}>
                        {r.fromCheckpoint?.name ?? '—'}
                    </Text>
                    {r.fromCheckpoint?.id && (
                        <Tooltip title="View QR Code">
                            <Button
                                size="small"
                                type="text"
                                icon={<QrcodeOutlined />}
                                onClick={() => openQr(r.fromCheckpoint)}
                            />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
        {
            title: '',
            key: 'arrow',
            width: 40,
            render: (_, r) => (
                <ArrowRightOutlined style={{ color: r.isRequired ? '#6366f1' : '#9ca3af' }} />
            ),
        },
        {
            title: 'To',
            key: 'to',
            render: (_, r) => (
                <Space size={6}>
                    <Text style={{ color: r.isActive ? 'inherit' : '#9ca3af' }}>
                        {r.toCheckpoint?.name ?? '—'}
                    </Text>
                    {r.toCheckpoint?.id && (
                        <Tooltip title="View QR Code">
                            <Button
                                size="small"
                                type="text"
                                icon={<QrcodeOutlined />}
                                onClick={() => openQr(r.toCheckpoint)}
                            />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
        {
            title: 'Expected',
            dataIndex: 'expectedMinutes',
            key: 'expected',
            render: (m, r) => (
                <span style={{ color: r.isActive ? 'inherit' : '#9ca3af' }}>
                    {m} min{r.graceMinutes > 0 ? ` +${r.graceMinutes} grace` : ''}
                </span>
            ),
        },
        {
            title: 'Alt. Group',
            dataIndex: 'alternativeGroup',
            key: 'altGroup',
            render: (g) => g ? <Tag color="cyan">{g}</Tag> : <Text type="secondary">—</Text>,
        },
        {
            title: 'Type',
            key: 'type',
            render: (_, r) => (
                <Space size={4}>
                    {r.isRequired ? (
                        <Tag color="purple">Required</Tag>
                    ) : (
                        <Tag color="default">Optional</Tag>
                    )}
                    {!r.isActive && <Tag color="red">Inactive</Tag>}
                </Space>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'right',
            render: (_, r) =>
                canWrite && (
                    <Space>
                        <Tooltip title="Edit">
                            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                        </Tooltip>
                        <Popconfirm
                            title="Remove this transition?"
                            description="Route warnings will be recalculated after removal."
                            okText="Remove"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => deleteMutation.mutate(r.id)}
                        >
                            <Tooltip title="Delete">
                                <Button size="small" icon={<DeleteOutlined />} danger />
                            </Tooltip>
                        </Popconfirm>
                    </Space>
                ),
        },
    ];

    return (
        <div>
            {/* Route warnings */}
            {warnings.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    icon={<WarningOutlined />}
                    message="Route Configuration Warnings"
                    description={
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {warnings.map((w, i) => (
                                <li key={i} style={{ fontSize: 12, marginBottom: 2 }}>{w}</li>
                            ))}
                        </ul>
                    }
                    style={{ marginBottom: 16 }}
                />
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Space>
                    <Text strong style={{ fontSize: 15 }}>Patrol Route Transitions</Text>
                    <Tag color="blue" style={{ marginLeft: 4 }}>{transitions.length} total</Tag>
                </Space>
                {canWrite && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined style={{ color: '#fff' }} />}
                        onClick={openCreate}
                        style={{
                            backgroundColor: colorPrimary,
                            border: 'none',
                            color: '#fff',
                            height: '36px',
                            borderRadius: '6px',
                            fontWeight: 500,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            boxShadow: 'none',
                        }}
                    >
                        Add Transition
                    </Button>
                )}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
                <Space size={4}><ArrowRightOutlined style={{ color: '#6366f1' }} /><Text style={{ fontSize: 11, color: '#6b7280' }}>Required</Text></Space>
                <Space size={4}><ArrowRightOutlined style={{ color: '#9ca3af' }} /><Text style={{ fontSize: 11, color: '#6b7280' }}>Optional</Text></Space>
                <Space size={4}><Tag color="cyan" style={{ fontSize: 10 }}>group-key</Tag><Text style={{ fontSize: 11, color: '#6b7280' }}>Alternative group (any one satisfies)</Text></Space>
            </div>

            <Spin spinning={isLoading}>
                <Table
                    dataSource={transitions}
                    columns={columns}
                    rowKey="id"
                    size="small"
                    pagination={{ pageSize: 15, hideOnSinglePage: true }}
                    scroll={{ x: 650 }}
                    rowClassName={(r) => (!r.isActive ? 'opacity-40' : '')}
                />
            </Spin>

            <Drawer
                title={editTarget ? 'Edit Transition' : 'Add Transition'}
                open={drawerOpen}
                onClose={closeDrawer}
                width={620}
                destroyOnClose
            >
                <TransitionForm
                    propertyId={propertyId}
                    checkpoints={activeCheckpoints}
                    existingGroups={existingGroups}
                    initialData={editTarget}
                    onSuccess={handleFormSuccess}
                    onCancel={closeDrawer}
                />
            </Drawer>

            <QrPanel
                checkpointId={qrTarget?.id}
                checkpointName={qrTarget?.name}
                checkpoint={qrTarget}
                visible={!!qrTarget}
                onClose={() => setQrTarget(null)}
            />
        </div>
    );
};

export default PatrolRouteTab;
